
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

export default function PreunderwritingAI() {
  const navigate = useNavigate();

  const features = [
    {
      icon: 'ri-robot-2-line',
      title: 'AI Document Analysis',
      description: 'Advanced AI algorithms analyze income documents, bank statements, and tax returns for instant qualification.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Risk Assessment',
      description: 'Automated risk scoring based on credit, income, debt-to-income ratios, and employment history.'
    },
    {
      icon: 'ri-speed-line',
      title: 'Real-time Processing',
      description: 'Instant pre-qualification results that help lenders make faster lending decisions.'
    },
    {
      icon: 'ri-file-check-line',
      title: 'Compliance Checking',
      description: 'Automated compliance verification ensures all applications meet regulatory requirements.'
    }
  ];

  const benefits = [
    {
      title: '90% Faster Processing',
      description: 'Reduce pre-underwriting time from hours to minutes with AI automation.',
      icon: 'ri-time-line'
    },
    {
      title: '95% Accuracy Rate',
      description: 'AI-powered analysis provides more accurate risk assessment than manual review.',
      icon: 'ri-target-line'
    },
    {
      title: '60% Cost Reduction',
      description: 'Significantly reduce operational costs while improving loan quality.',
      icon: 'ri-money-dollar-circle-line'
    },
    {
      title: '24/7 Processing',
      description: 'Automated system works around the clock to qualify borrowers instantly.',
      icon: 'ri-24-hours-line'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Document Upload',
      description: 'Borrowers upload required documents through secure AI-powered portal.'
    },
    {
      step: '02',
      title: 'AI Analysis',
      description: 'Advanced algorithms analyze documents and extract key financial data.'
    },
    {
      step: '03',
      title: 'Risk Scoring',
      description: 'AI generates comprehensive risk score and qualification recommendation.'
    },
    {
      step: '04',
      title: 'Instant Decision',
      description: 'Lenders receive instant pre-qualification results with detailed analysis.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(147, 51, 234, 0.8), rgba(126, 34, 206, 0.8)), url('https://readdy.ai/api/search-image?query=AI%20technology%20specialist%20demonstrating%20automated%20underwriting%20software%20on%20multiple%20computer%20screens%20showing%20document%20analysis%20and%20risk%20assessment%20dashboards%20in%20modern%20fintech%20office%20environment%20with%20digital%20interfaces%20and%20data%20visualization&width=1920&height=800&seq=ai-underwriting-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Pre-underwriting AI Solutions
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 mb-8">
              Advanced AI-powered pre-qualification and underwriting assistance that streamlines loan approval processes with intelligent document analysis and automated risk assessment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-white text-black hover:bg-gray-100"
              >
                <i className="ri-robot-line mr-2 text-black"></i>
                Get AI Demo
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={() => navigate('/case-studies')}
                className="border-white text-white hover:bg-white hover:text-purple-600"
              >
                <i className="ri-bar-chart-line mr-2"></i>
                View Case Studies
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Transform Your Underwriting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our AI solutions revolutionize traditional underwriting with speed, accuracy, and efficiency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-6">
                  <i className={`${benefit.icon} text-2xl text-purple-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Advanced AI Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cutting-edge artificial intelligence technology designed specifically for mortgage underwriting.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Advanced%20AI%20dashboard%20showing%20mortgage%20document%20analysis%20with%20machine%20learning%20algorithms%20processing%20loan%20applications%2C%20neural%20network%20visualization%2C%20automated%20risk%20scoring%20interface%2C%20futuristic%20fintech%20technology%20workspace&width=600&height=400&seq=ai-features&orientation=landscape"
                alt="AI Underwriting Dashboard"
                className="w-full rounded-2xl shadow-lg object-cover object-top"
              />
            </div>
            <div>
              <div className="space-y-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-lg mr-4 flex-shrink-0">
                      <i className={`${feature.icon} text-xl text-purple-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-purple-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How Our AI Pre-underwriting Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A streamlined process that transforms traditional underwriting with artificial intelligence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-purple-600 text-white text-2xl font-bold rounded-full mx-auto mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
            <div className="lg:w-1/2">
              <img 
                src="https://readdy.ai/api/search-image?query=Futuristic%20AI%20neural%20network%20processing%20mortgage%20documents%20with%20glowing%20data%20connections%2C%20machine%20learning%20algorithms%20analyzing%20financial%20data%2C%20automated%20underwriting%20system%20with%20holographic%20interfaces%20and%20data%20visualization%20charts&width=600&height=400&seq=ai-technology&orientation=landscape"
                alt="AI Technology"
                className="w-full rounded-2xl shadow-lg object-cover object-top"
              />
            </div>
            <div className="lg:w-1/2">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Next-Generation AI Technology
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our proprietary AI algorithms are trained on millions of loan applications, providing unmatched accuracy in pre-underwriting decisions.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-lg mr-4">
                    <i className="ri-check-line text-purple-600"></i>
                  </div>
                  <span className="text-gray-700">Machine learning trained on 10M+ loan files</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-lg mr-4">
                    <i className="ri-check-line text-purple-600"></i>
                  </div>
                  <span className="text-gray-700">Natural language processing for document analysis</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-lg mr-4">
                    <i className="ri-check-line text-purple-600"></i>
                  </div>
                  <span className="text-gray-700">Predictive analytics for risk assessment</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-lg mr-4">
                    <i className="ri-check-line text-purple-600"></i>
                  </div>
                  <span className="text-gray-700">Continuous learning and improvement</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-purple-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Revolutionize Your Underwriting?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Discover how our AI-powered pre-underwriting solutions can transform your lending process and improve loan quality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+910120418794"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black hover:bg-gray-100 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call +91 0120 418 7944
            </a>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => navigate('/services')}
              className="border-white text-white hover:bg-white hover:text-purple-600"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              All Services
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
